import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Gift } from "lucide-react";
import { useAuth, roleHome } from "@/context/AuthContext";
import { useCityTheme } from "@/context/CityThemeContext";
import { apiError } from "@/lib/api";
import { CoBrandRow, CityBrandWordmark } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const DEMOS = [
  { label: "Consumidor · João", email: "joao@teste.com.br", testId: "demo-consumer" },
  { label: "Lojista · Super Cachorro do Léo", email: "leo@supercachorro.com.br", testId: "demo-merchant" },
  { label: "Motoboy · Carlos", email: "motoboy.teste@magetimefood.com.br", testId: "demo-courier" },
  { label: "Motoboy 2 · Bruno", email: "motoboy.teste@magegastrofood.com.br", testId: "demo-courier-2" },
  { label: "Admin · Time Food (plataforma)", email: "admin@timefood.com.br", testId: "demo-admin" },
  { label: "Admin · Prefeitura de Magé", email: "prefeitura@mage.rj.gov.br", testId: "demo-prefeitura" },
];

export default function Login() {
  const { login, register } = useAuth();
  const { city, cityName, accent } = useCityTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", ref: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, ref: ref.toUpperCase() }));
      setMode("register");
    }
  }, [params]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register({ ...form, ref: form.ref || null, role: "consumer" });
      navigate(roleHome(user), { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const quick = async (email) => {
    setBusy(true);
    setError("");
    try {
      const user = await login(email, "demo1234");
      navigate(roleHome(user), { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gf-black flex flex-col" data-testid="login-screen">
      <div className="bg-white px-4 py-2 flex items-center justify-center gap-2 border-b-4" style={{ borderColor: "#2E8B57" }}>
        <span className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: accent }}>
          Prefeitura de {cityName}
        </span>
        <span className="text-mage-red">•</span>
        <span className="font-script text-base" style={{ color: accent }}>Governando com Amor!</span>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-7">
        <div className="w-full max-w-[430px] animate-rise">
          <div className="bg-white rounded-[28px] p-6 pt-7 shadow-2xl">
            <CoBrandRow city={city} size={58} />
            <p className="text-center text-[11px] text-gray-500 mt-3">
              Parceria oficial — Prefeitura de {cityName}
            </p>

            <div className="mt-6 text-left">
              <h1 className="font-display text-3xl font-extrabold leading-tight text-gf-black">Bem-vindo ao</h1>
              <CityBrandWordmark city={cityName} size={29} accent={accent} />
              <p className="text-sm text-gray-500 mt-2">
                Comida boa, entrega rápida — e todo mundo ganha indicando. Um só app para clientes, lojas,
                motoboys e a Prefeitura.
              </p>
            </div>

            {form.ref && (
              <div
                data-testid="referral-banner"
                className="mt-4 rounded-2xl bg-orange-50 border border-gf-orange p-3 flex items-center gap-2"
              >
                <Gift size={18} className="text-gf-orange" />
                <p className="text-xs font-semibold text-gf-black">
                  Você foi convidado com o código <b>{form.ref}</b> — crie sua conta e comece a ganhar também.
                </p>
              </div>
            )}

            <div className="mt-5 flex gap-1 bg-gf-gray p-1 rounded-full">
              {[["login", "Entrar"], ["register", "Criar conta"]].map(([m, l]) => (
                <button
                  key={m}
                  data-testid={`tab-${m}`}
                  onClick={() => { setMode(m); setError(""); }}
                  className={`flex-1 py-2 rounded-full text-sm font-bold gf-press ${mode === m ? "bg-white shadow text-gf-black" : "text-gray-500"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-4 space-y-3">
              {mode === "register" && (
                <>
                  <Input data-testid="register-name-input" placeholder="Seu nome" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-12 rounded-xl" />
                  <Input data-testid="register-phone-input" placeholder="Telefone (opcional)" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 rounded-xl" />
                  <Input data-testid="register-ref-input" placeholder="Código de indicação (opcional)" value={form.ref}
                    onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })} className="h-12 rounded-xl" />
                </>
              )}
              <Input data-testid="login-email-input" type="email" placeholder="E-mail" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required className="h-12 rounded-xl" />
              <Input data-testid="login-password-input" type="password" placeholder="Senha" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required className="h-12 rounded-xl" />
              {error && <p data-testid="login-error" className="text-sm font-semibold text-mage-red">{error}</p>}
              <Button data-testid="login-submit-button" type="submit" disabled={busy}
                className="w-full h-12 rounded-xl gf-gradient text-white font-bold text-base gf-press hover:opacity-95">
                {busy ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Entrar" : "Criar conta"}
              </Button>
            </form>

            <div className="mt-5 pt-4 border-t border-dashed border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                Acesso rápido de demonstração
              </p>
              <div className="grid gap-2">
                {DEMOS.map((d) => (
                  <button key={d.email} data-testid={d.testId} onClick={() => quick(d.email)} disabled={busy}
                    className="text-left text-xs font-semibold px-3 py-2.5 rounded-xl bg-gf-gray hover:bg-orange-50 border border-transparent hover:border-gf-orange gf-press">
                    {d.label}
                    <span className="block text-[10px] font-normal text-gray-500">{d.email} · demo1234</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-center text-[11px] text-gray-400 mt-4">
            {city?.brand_name || "Magé Time Food"} · software Time Food · patente em processo
          </p>
        </div>
      </div>
    </div>
  );
}
